import {
  ButtonIcon,
  Chip,
  Flex,
  IconMore,
  Menu,
  MenuRoot,
  MenuRow,
} from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';
import {
  LabelChips,
  type LabelDefinition,
} from '@spotify/confidence-common-react';
import {
  DateUtils,
  ResourceNameLink,
  ResourceTable,
} from '@spotify/confidence-core-react';
import {
  CollectionEntryFragment,
  type IdentityRefFragment,
} from '@spotify/confidence-graphql';
import { IdentityInline } from '@spotify/plugin-confidence-iam-react';

import {
  type CollectionEntryColumn,
  type CollectionEntryExtensionOptions,
  CollectionEntryOptions,
  getExtensionDisplayName,
} from '../../extensions';
import { FavoriteIconButton } from '../FavoriteIconButton';
import { HidableColumn } from './HidableColumn';

type CollectionEntryRowProps = {
  entry: CollectionEntryFragment;
  extension?: CollectionEntryExtensionOptions;
  columns?: CollectionEntryColumn[];
  labelDefinitions: LabelDefinition[];
  isColumnVisible: (columnId: string) => boolean;
  onRemoveEntry?: (entry: { name: string; displayName: string }) => void;
};

type EntryResource = CollectionEntryOptions['resource'];

const EmptyCell = () => <>-</>;

const getOwnerIdentity = (
  resource: EntryResource,
): IdentityRefFragment | undefined => {
  if (!('owner' in resource) || !resource.owner) return undefined;
  return resource.owner.__typename === 'IamV1Identity'
    ? resource.owner
    : undefined;
};

const getLabels = (
  resource: EntryResource,
): Array<{ key: string; value?: string | null }> | undefined => {
  if ('labels' in resource && Array.isArray(resource.labels)) {
    return resource.labels;
  }
  return undefined;
};

const OwnerCell = ({ resource }: { resource?: EntryResource }) => {
  if (!resource) return <EmptyCell />;
  const identity = getOwnerIdentity(resource);
  if (!identity) return <EmptyCell />;
  return <IdentityInline identityRef={identity} />;
};

const LabelsCell = ({
  resource,
  labelDefinitions,
}: {
  resource?: EntryResource;
  labelDefinitions: LabelDefinition[];
}) => {
  if (!resource) return <EmptyCell />;
  const labels = getLabels(resource);
  if (!labels) return <EmptyCell />;
  return <LabelChips labels={labels} labelDefinitions={labelDefinitions} />;
};

const ExtensionCell = ({
  column,
  resource,
}: {
  column: CollectionEntryColumn;
  resource: EntryResource;
}) => {
  if (column.Component) {
    return <column.Component resource={resource} />;
  }

  const value = column.getValue?.(resource);
  if (!value) return <EmptyCell />;

  if (Array.isArray(value)) {
    if (value.length === 0) return <EmptyCell />;
    return (
      <Flex gap="tighter-2" wrap="wrap">
        {value.map((v, i) => (
          <Chip key={i} size="small">
            {v}
          </Chip>
        ))}
      </Flex>
    );
  }

  return <>{value}</>;
};

export const CollectionEntryRow = ({
  entry,
  extension,
  columns,
  labelDefinitions,
  isColumnVisible,
  onRemoveEntry,
}: CollectionEntryRowProps) => {
  const resource = entry.resource ?? undefined;
  const displayName = resource
    ? getExtensionDisplayName(extension, resource, entry)
    : entry.name;
  const resourceName =
    resource && 'name' in resource ? String(resource.name) : entry.name;

  const ResourceTypeLabel = extension?.ResourceTypeLabel;

  return (
    <ResourceTable.Row
      routeRef={extension?.routeRef}
      name={resourceName}
      params={extension?.getRouteParams?.(resourceName)}
    >
      <HidableColumn id="favorite" isVisible={isColumnVisible}>
        <ResourceTable.Cell onClick={e => e.stopPropagation()} data-track-source-anchor="feadc4510e;collection-entry-row-cell">
          <FavoriteIconButton
            resourceName={resourceName}
            resourceLabel={displayName}
           data-track-source-anchor="3226d49bf5;collection-entry-row-favorite-icon-button"/>
        </ResourceTable.Cell>
      </HidableColumn>
      <HidableColumn id="name" isVisible={isColumnVisible}>
        <ResourceTable.Cell emphasized>
          <ResourceNameLink name={resourceName} text={displayName}  data-track-source-anchor="d33e6af40a;collection-entry-row-resource-name-link"/>
        </ResourceTable.Cell>
      </HidableColumn>
      <HidableColumn id="type" isVisible={isColumnVisible}>
        <ResourceTable.Cell>
          {ResourceTypeLabel && resource ? (
            <ResourceTypeLabel resource={resource} />
          ) : (
            (extension?.resourceType ?? 'Unknown')
          )}
        </ResourceTable.Cell>
      </HidableColumn>
      {columns?.map(col => (
        <ResourceTable.Cell key={col.id}>
          {resource ? <ExtensionCell column={col} resource={resource} /> : '-'}
        </ResourceTable.Cell>
      ))}
      <HidableColumn id="owner" isVisible={isColumnVisible}>
        <ResourceTable.Cell>
          <OwnerCell resource={resource} />
        </ResourceTable.Cell>
      </HidableColumn>
      <HidableColumn id="labels" isVisible={isColumnVisible}>
        <ResourceTable.Cell>
          <LabelsCell resource={resource} labelDefinitions={labelDefinitions} />
        </ResourceTable.Cell>
      </HidableColumn>
      <HidableColumn id="added" isVisible={isColumnVisible}>
        <ResourceTable.Cell>
          {entry.createTime ? DateUtils.xAgo(new Date(entry.createTime)) : '-'}
        </ResourceTable.Cell>
      </HidableColumn>
      <ResourceTable.Cell onClick={e => e.stopPropagation()} data-track-source-anchor="d1954d4702">
        <MenuRoot>
          <ButtonIcon
            aria-label="Entry actions"
            size="small"
            variant="tertiary"
          >
            <IconMore size="small" />
          </ButtonIcon>
          <Popover hasNoArrow>
            <Menu aria-label="Entry actions">
              <MenuRow
                id="remove"
                textValue="Remove from collection"
                onAction={() =>
                  onRemoveEntry?.({ name: entry.name, displayName })
                }
               data-track-source-anchor="6d6627bff4;;high">
                Remove from collection
              </MenuRow>
            </Menu>
          </Popover>
        </MenuRoot>
      </ResourceTable.Cell>
    </ResourceTable.Row>
  );
};
